// En-tete : compactage au scroll, mega-menu, tiroir de navigation et
// recherche predictive. Web Component, sans dependance.

const sobre = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

class MxEntete extends HTMLElement {
  connectedCallback() {
    this.seuil = Number(this.dataset.compacteA || 30);
    this.brancheCompactage();
    this.brancheMega();
    this.brancheRecherche();
    this.brancheTiroirMenu();
  }

  /* ---------- Compactage au scroll ---------- */
  brancheCompactage() {
    const maj = () => {
      this.dataset.compacte = String(scrollY > this.seuil);
    };
    maj();
    addEventListener('scroll', maj, { passive: true });
  }

  /* ---------- Mega-menu ---------- */
  brancheMega() {
    this.megas = [...this.querySelectorAll('[data-mega]')].map((bouton) => ({
      bouton,
      panneau: this.querySelector(`#${bouton.getAttribute('aria-controls')}`),
    }));

    this.megas.forEach(({ bouton, panneau }) => {
      if (!panneau) return;

      bouton.addEventListener('click', () => {
        const ouvert = bouton.getAttribute('aria-expanded') === 'true';

        // Sur un appareil a survol, le pointeur a deja ouvert le panneau
        // avant que le clic n'arrive : sans ce garde-fou, le clic le
        // refermait aussitot et le menu paraissait inerte.
        if (ouvert && this.ouvertParSurvol === bouton) {
          this.ouvertParSurvol = null;
          return;
        }

        this.fermeMegas();
        if (!ouvert) {
          bouton.setAttribute('aria-expanded', 'true');
          panneau.hidden = false;
        }
      });

      // Au survol, le menu s'ouvre sans clic : plus direct a la souris.
      const entree = bouton.closest('.mx-entete__entree');
      let minuterie;
      entree.addEventListener('pointerenter', (ev) => {
        if (ev.pointerType === 'touch') return;
        clearTimeout(minuterie);
        this.fermeMegas();
        bouton.setAttribute('aria-expanded', 'true');
        panneau.hidden = false;
        this.ouvertParSurvol = bouton;
      });
      entree.addEventListener('pointerleave', (ev) => {
        if (ev.pointerType === 'touch') return;
        minuterie = setTimeout(() => this.fermeMegas(), 120);
      });
    });

    // Clic en dehors et touche Echap referment.
    document.addEventListener('click', (ev) => {
      if (!ev.target.closest('.mx-entete__entree--mega')) this.fermeMegas();
    });
    document.addEventListener('keydown', (ev) => {
      if (ev.key !== 'Escape') return;
      const ouvert = this.megas.find(({ bouton }) => bouton.getAttribute('aria-expanded') === 'true');
      if (ouvert) { this.fermeMegas(); ouvert.bouton.focus(); }
    });
  }

  fermeMegas() {
    this.ouvertParSurvol = null;
    this.megas?.forEach(({ bouton, panneau }) => {
      bouton.setAttribute('aria-expanded', 'false');
      if (panneau) panneau.hidden = true;
    });
  }

  /* ---------- Recherche predictive ---------- */
  brancheRecherche() {
    this.panneau = this.querySelector('[data-recherche]');
    this.champ = this.querySelector('[data-champ-recherche]');
    this.resultats = this.querySelector('[data-resultats]');
    if (!this.panneau || !this.champ) return;

    this.querySelector('[data-ouvre-recherche]')?.addEventListener('click', () => {
      const ouvert = !this.panneau.hidden;
      this.panneau.hidden = ouvert;
      if (!ouvert) this.champ.focus();
    });

    this.querySelector('[data-ferme-recherche]')?.addEventListener('click', () => {
      this.panneau.hidden = true;
      this.champ.value = '';
      this.resultats.textContent = '';
    });

    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && !this.panneau.hidden) {
        this.panneau.hidden = true;
      }
    });

    let minuterie;
    let requete;
    this.champ.addEventListener('input', () => {
      clearTimeout(minuterie);
      const q = this.champ.value.trim();

      if (q.length < 2) {
        this.resultats.textContent = '';
        return;
      }

      minuterie = setTimeout(async () => {
        // Annule la requete precedente : seule la derniere compte.
        requete?.abort();
        requete = new AbortController();

        const url = new URL('/search/suggest.json', location.origin);
        url.searchParams.set('q', q);
        url.searchParams.set('resources[type]', 'product,collection,page');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][unavailable_products]', 'last');

        try {
          const rep = await fetch(url, { signal: requete.signal });
          if (!rep.ok) throw new Error(rep.status);
          this.dessineResultats((await rep.json()).resources.results, q);
        } catch (e) {
          if (e.name !== 'AbortError') this.resultats.textContent = '';
        }
      }, 220);
    });
  }

  dessineResultats(res, q) {
    const prix = (c) => (c / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
    const echappe = (s) => String(s ?? '').replace(/[<>&"]/g, (c) =>
      ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c]);

    const morceaux = [];

    if (res.products?.length) {
      morceaux.push(`<div class="mx-recherche__groupe"><h3>Bonbons</h3><div class="mx-recherche__liste">${
        res.products.map((p) => `
          <a class="mx-recherche__item" href="${echappe(p.url)}">
            <span class="mx-photo">${
              p.featured_image?.url
                ? `<img src="${echappe(p.featured_image.url)}" alt="" width="88" height="88" loading="lazy">`
                : ''
            }</span>
            <span class="mx-recherche__nom">${echappe(p.title)}</span>
            <span class="mx-recherche__prix">${p.price ? `dès ${prix(Number(p.price))}` : ''}</span>
          </a>`).join('')
      }</div></div>`);
    }

    if (res.collections?.length) {
      morceaux.push(`<div class="mx-recherche__groupe"><h3>Catégories</h3><div class="mx-recherche__liste">${
        res.collections.map((c) => `
          <a class="mx-recherche__item" href="${echappe(c.url)}">
            <span class="mx-recherche__nom">${echappe(c.title)}</span>
          </a>`).join('')
      }</div></div>`);
    }

    if (res.pages?.length) {
      morceaux.push(`<div class="mx-recherche__groupe"><h3>Pages</h3><div class="mx-recherche__liste">${
        res.pages.map((p) => `
          <a class="mx-recherche__item" href="${echappe(p.url)}">
            <span class="mx-recherche__nom">${echappe(p.title)}</span>
          </a>`).join('')
      }</div></div>`);
    }

    if (!morceaux.length) {
      this.resultats.innerHTML = `<p class="mx-recherche__vide">Aucun résultat pour « ${echappe(q)} ».</p>`;
      return;
    }

    morceaux.push(`<a class="mx-recherche__tout" href="/search?q=${encodeURIComponent(q)}">Voir tous les résultats</a>`);
    this.resultats.innerHTML = morceaux.join('');
  }

  /* ---------- Tiroir de navigation ---------- */
  brancheTiroirMenu() {
    const tiroir = document.querySelector('[data-menu-tiroir]');
    const voile = document.querySelector('[data-voile-menu]');
    const burger = this.querySelector('[data-ouvre-menu]');
    if (!tiroir || !voile || !burger) return;

    const ouvre = () => {
      tiroir.hidden = false;
      voile.hidden = false;
      // Laisse le navigateur peindre l'etat ferme avant de glisser.
      requestAnimationFrame(() => {
        tiroir.dataset.ouvert = 'true';
        voile.dataset.ouvert = 'true';
      });
      burger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      tiroir.querySelector('a, button')?.focus();
    };

    const ferme = () => {
      tiroir.dataset.ouvert = 'false';
      voile.dataset.ouvert = 'false';
      burger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      const apres = () => { tiroir.hidden = true; voile.hidden = true; };
      if (sobre()) apres();
      else setTimeout(apres, 300);
      burger.focus();
    };

    burger.addEventListener('click', ouvre);
    voile.addEventListener('click', ferme);
    tiroir.querySelector('[data-ferme-menu]')?.addEventListener('click', ferme);
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && tiroir.dataset.ouvert === 'true') ferme();
    });
  }
}

customElements.define('mx-entete', MxEntete);
